import { memo, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Dialog from "components/DialogV2";
import DialogHeader from "components/DialogV2/DialogHeader";
import DialogTitle from "components/DialogV2/DialogTitle";
import DialogClose from "components/DialogV2/DialogClose";
import DialogContent from "components/DialogV2/DialogContent";
import DialogActions from "components/DialogV2/DialogActions";
import Button from "components/Button";
import Typography from "components/Typography";
import Div from "components/primitives/Div";
import Checkbox from "components/Inputs/Checkbox";
import { useFormik } from "formik";
import useNumberFormatter from "hooks/useNumberFormatter";
import CommonSchemas from "schemas/CommonSchemas";
import useGetAthletesMeasurementsCount from "hooks/useGetAthletesMeasurementsCount";
import { Athlete } from "types/athlete";
import AthleteHelpers from "helpers/AthleteHelpers";
import Warning from "components/Warning";
import { Label } from "components/Inputs/Label";

interface DeleteAthleteDialogProps {
  open: boolean;
  athlete: Athlete;
  onClose: () => void;
  onDelete: () => void;
}

function DeleteAthleteDialog(props: DeleteAthleteDialogProps) {
  const { athlete, onDelete, onClose, open } = props;
  const { t } = useTranslation();
  const { format } = useNumberFormatter();

  const { data, status } = useGetAthletesMeasurementsCount([athlete.id], { enabled: open });

  const formik = useFormik({
    initialValues: { confirmation: false },
    validationSchema: CommonSchemas.confirmationValidationSchema,
    onSubmit: async (_, helpers) => {
      onDelete();
      helpers.resetForm();
    },
  });

  const handleOnClose = useCallback(() => {
    onClose();
    formik.resetForm();
  }, [formik, onClose]);

  const athleteName = useMemo(() => AthleteHelpers.getFullName(athlete), [athlete]);

  const totalMeasurements = useMemo(() => {
    if (status === "fetching") return undefined;
    return format(data.count);
  }, [status, format, data.count]);

  return (
    <Dialog open={open} onClose={handleOnClose} width={500}>
      <DialogHeader>
        <DialogTitle>{t("athlete.bulk_delete_button_text_one")}</DialogTitle>
        <DialogClose />
      </DialogHeader>
      <DialogContent sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
        <Div sx={{ display: "flex", alignItems: "center", gap: 1.5, flexDirection: "column", mb: 2 }}>
          <Div>
            <Typography textAlign="center" fontWeight="fontWeightMedium" data-sensitive>
              {t("global.are_you_sure_to_delete_name", { name: athleteName })}
            </Typography>
          </Div>
          <Warning>
            <Typography variant="TextS" fontWeight="fontWeightMedium">
              {t("athlete.bulk_delete_athlete_measurements_count", {
                total: totalMeasurements,
                count: data.count,
              })}
            </Typography>
          </Warning>
          <Div>
            <Typography variant="TextS" textAlign="center">
              {t("athlete.bulk_delete_dialog_warning_text")}
            </Typography>
          </Div>
        </Div>

        <form id="confirmation-form" data-testid="confirmation-form" onSubmit={formik.handleSubmit}>
          <Div sx={{ display: "flex", alignItems: "center" }}>
            <Checkbox
              id="confirmation"
              aria-describedby="confirmation-label"
              value={formik.values.confirmation}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={Boolean(formik.touched.confirmation && formik.errors.confirmation)}
            />
            <Label id="confirmation-label" htmlFor="confirmation">
              {t("athlete.bulk_delete_dialog_confirm_checkbox_text_one")}
            </Label>
          </Div>
          {formik.touched.confirmation && !!formik.errors.confirmation ? (
            <Typography variant="TextXS" fontWeight="fontWeightMedium" sx={{ color: "error.main", paddingLeft: 3.75 }}>
              {t(formik.errors.confirmation)}
            </Typography>
          ) : null}
        </form>
      </DialogContent>
      <DialogActions sx={{ flexDirection: "column", gap: 2, pt: 2 }}>
        <Button
          type="submit"
          form="confirmation-form"
          variant="danger"
          size="small"
          fullWidth
          disabled={status === "fetching"}
          loading={status === "fetching"}
        >
          {t("athlete.bulk_delete_button_text_one")}
        </Button>
        <Button variant="tertiary" size="small" fullWidth onClick={handleOnClose}>
          {t("global.cancel_button_text")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default memo(DeleteAthleteDialog);
